import { blue, green, grey, red, yellow } from "@mui/material/colors";
import { IAgendaTypeConfig, IAgendasConfigParams } from "../constants/types";
import { RequestStatus } from "../pages/hospital/Request/types";

export function canCancelAppointment(appointmentDate:Date, appointmentStatus:RequestStatus, outpatientsInfo:IAgendaTypeConfig): boolean {
    // SHER-458: once an appointment is checked-in / completed (green tick), it
    // must not be cancellable — cancelling after check-in creates billing and
    // workflow inconsistencies. `isAppointmentDone` covers the green-tick
    // statuses (ACCEPTED, COMPLETED); CHECKED_IN is the explicit checked-in one.
    if(isAppointmentDone(appointmentStatus) || appointmentStatus === RequestStatus.CHECKED_IN){
        return false;
    }
    if(outpatientsInfo.type === "date"){
        const isTodayOrFutureDate = (appointmentDate > new Date() || appointmentDate.toDateString() === new Date().toDateString());
        console.log("isTodayOrFutureDate", isTodayOrFutureDate);
        return isTodayOrFutureDate && ([RequestStatus.CANCELED_BY_USER, RequestStatus.ACCEPTED, RequestStatus.CANCELED].includes(appointmentStatus) === false);
    }
    else if(outpatientsInfo.type === "date_time"){
        return appointmentDate > new Date() && ([RequestStatus.ACCEPTED, RequestStatus.CANCELED, RequestStatus.CANCELED_BY_USER, RequestStatus.CHECKED_IN].includes(appointmentStatus) === false);
    }
    return false;
}

export function canShowUpAppointment(appointmentDate:Date, appointmentStatus:RequestStatus, outpatientsInfo:IAgendaTypeConfig): boolean{
    const isToday = (appointmentDate.toDateString() === new Date().toDateString());
    return isToday && [RequestStatus.PENDING_APPROVAL].includes(appointmentStatus);
}

export function isAppointmentDone(status:number){
    return [RequestStatus.ACCEPTED, RequestStatus.COMPLETED].includes(status);
}

/**
 * Whether an appointment can be rescheduled (drag/move). Matches backend rules:
 * - Cannot reschedule completed or cancelled appointments.
 */
export function canRescheduleAppointment(status: RequestStatus): boolean {
    return (
        status !== RequestStatus.COMPLETED &&
        status !== RequestStatus.CANCELED &&
        status !== RequestStatus.CANCELED_BY_USER
    );
}

export function turnsAgendaDates(turns:number[][]):Date[]{
    const today = new Date();
    const startTurn = new Date(today);
    startTurn.setHours(turns[0][0]);
    startTurn.setMinutes(turns[0][1]);
    const endTurn = new Date(today);
    endTurn.setHours(turns[1][0]);
    endTurn.setMinutes(turns[1][1]);
    return [startTurn, endTurn];

}

export function errorCodesCreateAppointment(error: number | string){
    const errorTranslationPath = "pages.hospital.outpatients.appointment.error";
    if (error === "PAST_DATE") {
        return `${errorTranslationPath}.past_date`;
    }
    if (error === "DATE_BLOCKED") {
        return `${errorTranslationPath}.date_blocked`;
    }
    if (error === "WRONG_DAY") {
        return `${errorTranslationPath}.week_day_not_available`;
    }
    if (error === "AGENDA_FULL") {
        return `${errorTranslationPath}.full_agenda`;
    }
    if (error === "OUTSIDE_TURNS_AGENDA") {
        return `${errorTranslationPath}.outside_agenda_hours`;
    }
    if (error === "OVERLAPPING_APPOINTMENTS") {
        return `${errorTranslationPath}.conflict_appointment`;
    }
    if (error === "BILLABLE_AMOUNT_NOT_FOUND") {
        return `${errorTranslationPath}.billable_amount_not_found`;
    }
    if (error === "BILLABLE_AMOUNT_NOT_FOUND_UNINSURED") {
        return `${errorTranslationPath}.billable_amount_not_found_uninsured`;
    }
    let message = "";
    if(error === 0){
        message = `${errorTranslationPath}.date_time`
    }
    else if(error === 1){
        message = `${errorTranslationPath}.full_agenda`
    }
    else if(error === 2){
        message = `${errorTranslationPath}.date_blocked`
    }
    else if(error === 3){
        message = `${errorTranslationPath}.week_day_not_available`
    }
    else if(error === 4){
        message = `${errorTranslationPath}.outside_agenda_hours`
    }
    else if(error === 5){
        message = `${errorTranslationPath}.conflict_appointment`
    }
    else if(error === 6){
        message = `${errorTranslationPath}.request_has_appointment`
    }
    else if(error === 7){
        message = `${errorTranslationPath}.request_canceled`
    }
    else{
        return "general.error";
    }
    return message;
}


export function isTimeWithinAgendaHours(time: Date, agendaTurns: number[][]): boolean {
    const timeHours = time.getHours();
    const timeMinutes = time.getMinutes();
    const timeInMinutes = timeHours * 60 + timeMinutes;
    
    // Check each turn pair (turn[0]/turn[1], turn[2]/turn[3], etc.)
    for (let i = 0; i < agendaTurns.length; i += 2) {
        if (i + 1 < agendaTurns.length) {
            const startTurn = agendaTurns[i];
            const endTurn = agendaTurns[i + 1];
            
            const startTimeInMinutes = startTurn[0] * 60 + startTurn[1];
            const endTimeInMinutes = endTurn[0] * 60 + endTurn[1];
            
            if (timeInMinutes >= startTimeInMinutes && timeInMinutes < endTimeInMinutes) {
                return true;
            }
        }
    }
    
    return false;
}

export function isDateAvailableForAgenda(date: Date, agenda: any): boolean {
    // Check if the day of the week is available
    const currentWeekDay = date.toLocaleDateString("en-En", { weekday: 'short' });
    const isAnOfficeDay = agenda.daysWeek.includes(currentWeekDay);
    
    // Check if the date is blocked
    const dateObj = new Date(date.getTime());
    dateObj.setUTCHours(0, 0, 0, 0);
    const isBlockedDate = agenda.blockedDates.some((blockedTS: number) => {
        const blockedDate = new Date(blockedTS);
        return blockedDate.toDateString() === dateObj.toDateString();
    });
    
    return isAnOfficeDay && !isBlockedDate;
}

export function isTimeSlotAvailableInAnyAgenda(time: Date, agendas: any[], resourceId?: number): boolean {
    // If resourceId is specified, check only that agenda
    if (resourceId) {
        const agenda = agendas.find(a => (a.id || a.uuid) === resourceId);
        if (!agenda) return false;
        
        // First check if the date itself is available for this agenda
        if (!isDateAvailableForAgenda(time, agenda)) {
            return false;
        }
        
        // Then check if the time is within agenda hours
        return isTimeWithinAgendaHours(time, agenda.turn);
    }
    
    // Otherwise, check if time is available in any agenda
    return agendas.some(agenda => {
        return isDateAvailableForAgenda(time, agenda) && isTimeWithinAgendaHours(time, agenda.turn);
    });
}

export const requestStatusToColor = (status: RequestStatus) => {
    switch (status) {
        case RequestStatus.PENDING_APPROVAL:
            return blue[100];
        // In Progress States
        case RequestStatus.PENDING_PAYMENT:
        case RequestStatus.IN_PROGRESS:
        case RequestStatus.PENDING_ANESTHESIOLOGIST:
        case RequestStatus.PENDING_PURCHASE_SURGERY_PROTHESIS:
        // SHER-935: these reach the surgery calendar like any other surgical status. Left out
        // they fall to the default grey, which this switch means as "unknown state".
        case RequestStatus.PENDING_PAYMENT_AFTER_VPA:
        
            return yellow[900];
        case RequestStatus.CANCELED_BY_USER:
            return yellow[700];
        // Accepted States    
        case RequestStatus.ACCEPTED:
            return blue[600];
        case RequestStatus.SOME_ACCEPTED:
        case RequestStatus.INCOMPLETE_ACCEPTED:
            return blue[600];
        
        // Completed States
        case RequestStatus.COMPLETED:
        case RequestStatus.CHECKED_IN:
        case RequestStatus.ANESTHESIOLOGIST_ACCEPTED:
        case RequestStatus.PAYMENT_COMPLETED:
        case RequestStatus.PAYMENT_COMPLETED_AFTER_VPA:
        case RequestStatus.READY_FOR_SURGERY:
            return green[700];
        
        // Rejected/Error States
        case RequestStatus.DENIED:
        case RequestStatus.INCOMPLETE:
        case RequestStatus.ANESTHESIOLOGIST_REJECTED:
        case RequestStatus.EXPIRED:
            return red[500];
        
        // Default case
        default:
            return grey[400]; // Light gray for unknown states
    }
}
// SHER-407: capitalise each word of a calendar event's title for display.
//
// The cardio24 `/outpatients` calendar crashed with
// "Cannot read properties of null (reading 'charAt')" because an appointment
// event could arrive with a null/undefined title (a patient or service with no
// name). Guard the input so a missing title renders as an empty string instead
// of throwing and taking the whole outpatients page to the error boundary.
export function capitalizeFirstLetter(value: string | null | undefined): string {
    if (!value) return '';
    return value
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
}
